import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { format } from 'date-fns';
import { CalendarIcon, Save, ArrowLeft } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import VoiceNote from './VoiceNote';

const EditInteractionForm = ({ interaction, contactId, contactName, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    interaction_type: 'meeting',
    date: '',
    city: '',
    location: '',
    setup_method: '',
    connected_by: '',
    connected_by_company: '',
    key_points: '',
    questions_asked: '',
    quotes: '',
    future_course_of_action: '',
    is_summary: false,
    summary_text: ''
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (interaction) {
      setFormData({
        interaction_type: interaction.interaction_type || 'meeting',
        date: interaction.date || '',
        city: interaction.city || '',
        location: interaction.location || '',
        setup_method: interaction.setup_method || '',
        connected_by: interaction.connected_by || '',
        connected_by_company: interaction.connected_by_company || '',
        key_points: interaction.key_points || '',
        questions_asked: interaction.questions_asked || '',
        quotes: interaction.quotes || '',
        future_course_of_action: interaction.future_course_of_action || '',
        is_summary: interaction.is_summary || false,
        summary_text: interaction.summary_text || ''
      });
    }
  }, [interaction]);

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="max-w-3xl animate-fadeIn space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" onClick={onCancel} className="gap-2 text-slate-500 hover:text-[#0F172A]">
          <ArrowLeft className="w-4 h-4" />
          Back to {contactName}
        </Button>
      </div>

      {formData.is_summary ? (
        /* Summary Note Edit */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
              Edit Quick Note
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Date</Label>
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-[240px] justify-start text-left font-normal ${!formData.date ? 'text-muted-foreground' : ''}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(new Date(formData.date + 'T12:00:00'), 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date ? new Date(formData.date + 'T12:00:00') : undefined}
                    onSelect={(date) => {
                      updateField('date', date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '');
                      setShowDatePicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Summary</Label>
              <Input
                value={formData.summary_text}
                onChange={(e) => updateField('summary_text', e.target.value)}
                placeholder="Quick summary of the interaction..."
              />
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Full Interaction Edit */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
              Edit Interaction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Meeting or Call */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Interaction Type</Label>
              <RadioGroup
                value={formData.interaction_type}
                onValueChange={(v) => updateField('interaction_type', v)}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="meeting" id="edit-type-meeting" />
                  <Label htmlFor="edit-type-meeting" className="cursor-pointer">Meeting</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="call" id="edit-type-call" />
                  <Label htmlFor="edit-type-call" className="cursor-pointer">Call</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Date, City, Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Date</Label>
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!formData.date ? 'text-muted-foreground' : ''}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(new Date(formData.date + 'T12:00:00'), 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date ? new Date(formData.date + 'T12:00:00') : undefined}
                      onSelect={(date) => {
                        updateField('date', date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '');
                        setShowDatePicker(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">City of Meeting</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="e.g., Singapore"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="e.g., Marina Bay Sands"
                />
              </div>
            </div>

            {/* Setup Method */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">How Was This Set Up?</Label>
              <RadioGroup
                value={formData.setup_method}
                onValueChange={(v) => updateField('setup_method', v)}
                className="flex flex-wrap gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_them" id="edit-setup-them" />
                  <Label htmlFor="edit-setup-them" className="cursor-pointer">He/She reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_me" id="edit-setup-me" />
                  <Label htmlFor="edit-setup-me" className="cursor-pointer">I reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="connected_by" id="edit-setup-connected" />
                  <Label htmlFor="edit-setup-connected" className="cursor-pointer">Connected by someone</Label>
                </div>
              </RadioGroup>

              {formData.setup_method === 'connected_by' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    value={formData.connected_by}
                    onChange={(e) => updateField('connected_by', e.target.value)}
                    placeholder="Connected by whom?"
                  />
                  <Input
                    value={formData.connected_by_company}
                    onChange={(e) => updateField('connected_by_company', e.target.value)}
                    placeholder="Their company"
                  />
                </div>
              )}
            </div>

            {/* Key Points */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-medium">Key Points of Meeting</Label>
                <VoiceNote
                  onTranscription={(text) => {
                    const current = formData.key_points || '';
                    const separator = current ? '\n' : '';
                    updateField('key_points', current + separator + text);
                  }}
                />
              </div>
              <RichTextEditor
                value={formData.key_points}
                onChange={(value) => updateField('key_points', value)}
                placeholder="Add detailed notes about the meeting..."
              />
            </div>

            {/* Questions Asked */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Questions Asked <span className="text-slate-400 font-normal">(optional)</span></Label>
              <RichTextEditor
                value={formData.questions_asked}
                onChange={(value) => updateField('questions_asked', value)}
                placeholder="Questions asked during the meeting..."
              />
            </div>

            {/* Quotes */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Quotes <span className="text-slate-400 font-normal">(if any)</span></Label>
              <RichTextEditor
                value={formData.quotes}
                onChange={(value) => updateField('quotes', value)}
                placeholder="Notable quotes from the meeting..."
              />
            </div>

            {/* Future Course of Action */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Future Course of Action</Label>
              <RichTextEditor
                value={formData.future_course_of_action}
                onChange={(value) => updateField('future_course_of_action', value)}
                placeholder="Next steps, follow-ups, action items..."
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
        >
          <Save className="w-4 h-4" />
          Update Interaction
        </Button>
      </div>
    </div>
  );
};

export default EditInteractionForm;
